import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Bookmark, BookmarkPlus, Search, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import {
  Sheet, SheetContent, SheetTrigger,
} from '../ui/sheet';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSavedViewActions } from '../../hooks/useSavedViewActions';
import SaveViewDialog from './saved-views/SaveViewDialog';
import EditViewDialog from './saved-views/EditViewDialog';
import ScheduleViewDialog from './saved-views/ScheduleViewDialog';
import SavedViewRow from './saved-views/SavedViewRow';
import ExportColumnPicker from './saved-views/ExportColumnPicker';
import ViewActivityLogDialog from './saved-views/ViewActivityLogDialog';
import SavedViewAnalyticsModal from './saved-views/SavedViewAnalyticsModal';
import SortableViewGroup from './saved-views/SortableViewGroup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAVEABLE_ROUTES = {
  '/work-orders': 'Work Orders',
  '/assets': 'Assets',
  '/inspections': 'Inspections',
  '/operations': 'Operations',
  '/vendors': 'Vendors',
  '/ppm': 'Preventive Maintenance',
  '/budget': 'Budget',
};
const routeLabel = (p) => SAVEABLE_ROUTES[p] || p;

const SavedViewsMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [views, setViews] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Save dialog
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [autoSlug, setAutoSlug] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit + Schedule dialogs hold their own form state; parent only tracks the
  // current view target.
  const [editView, setEditView] = useState(null);
  const [schedView, setSchedView] = useState(null);

  // Column-picker + activity-log + analytics dialogs
  const [exportTarget, setExportTarget] = useState(null);  // { view, format }
  const [activityView, setActivityView] = useState(null);
  const [analyticsView, setAnalyticsView] = useState(null);

  const onSaveablePage = location.pathname in SAVEABLE_ROUTES;
  const hasActiveFilters = Boolean(location.search && location.search !== '?');
  const canSaveCurrent = onSaveablePage && hasActiveFilters;
  const canShare = user?.role === 'admin' || user?.role === 'facility_manager';

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchViews = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/saved-views`, { headers: authHeaders() });
      setViews(Array.isArray(data) ? data : []);
    } catch (err) {
      logger.debug('fetch saved views failed', err);
    }
  }, [user, authHeaders]);

  useEffect(() => { fetchViews(); }, [fetchViews]);

  // Listen for a "open save view" hint dispatched by the SmartSuggestionBanner
  // so its Save action opens the proper validated dialog instead of window.prompt.
  useEffect(() => {
    const handler = () => {
      if (location.pathname in SAVEABLE_ROUTES && location.search) {
        setOpen(false);
        setSaveDialogOpen(true);
      }
    };
    window.addEventListener('fp:open-save-view', handler);
    return () => window.removeEventListener('fp:open-save-view', handler);
  }, [location.pathname, location.search]);

  const visibleViews = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return views;
    return views.filter((v) =>
      (v.name || '').toLowerCase().includes(q)
      || (v.folder || '').toLowerCase().includes(q)
      || (v.tags || []).some((t) => t.toLowerCase().includes(q))
      || routeLabel(v.path).toLowerCase().includes(q)
    );
  }, [views, search]);

  const grouped = useMemo(() => {
    const out = {};
    for (const v of visibleViews) {
      const key = v.folder ? `📁 ${v.folder}` : (v.path || '/');
      (out[key] = out[key] || []).push(v);
    }
    return out;
  }, [visibleViews]);

  // For folder headers, surface a representative colour swatch (first view's folder_color)
  const folderColorByKey = useMemo(() => {
    const out = {};
    for (const v of visibleViews) {
      if (v.folder && v.folder_color && !out[`📁 ${v.folder}`]) {
        out[`📁 ${v.folder}`] = v.folder_color;
      }
    }
    return out;
  }, [visibleViews]);

  const topUsedId = useMemo(() => {
    const sorted = [...views].filter((v) => (v.access_count || 0) > 0)
      .sort((a, b) => (b.access_count || 0) - (a.access_count || 0));
    return sorted[0]?.id;
  }, [views]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Please enter a name for this view'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/saved-views`, {
        name: trimmed,
        path: location.pathname,
        query_string: location.search.replace(/^\?/, ''),
        shared: canShare ? shared : false,
        auto_slug: autoSlug,
      }, { headers: authHeaders() });
      setViews((prev) => [data, ...prev]);
      setSaveDialogOpen(false);
      setName(''); setShared(false); setAutoSlug(false);
      if (data.slug) {
        const fullUrl = `${window.location.origin}/v/${data.slug}`;
        navigator.clipboard.writeText(fullUrl).catch(() => {});
        toast.success(`Saved “${data.name}” · permalink ${fullUrl} copied`);
      } else {
        toast.success(`Saved “${data.name}”`);
      }
    } catch (err) {
      logger.error('save view failed', err);
      toast.error(err.response?.data?.detail || 'Failed to save view');
    } finally { setSaving(false); }
  };

  // 12 row/action callbacks (subscribe / schedule / default / edit / delete /
  // copy / open-in-new-tab / apply / etc.) live in a custom hook so they're
  // stable references for `React.memo` on SavedViewRow.
  const actions = useSavedViewActions({
    authHeaders, navigate, setViews, setOpen, setEditView, setSchedView,
  });

  // The Edit/Schedule dialog targets are owned here, but the submit closures
  // need them. Bridge them through small wrappers.
  const onEditSubmit = useCallback(
    (payload) => actions.submitEdit(editView, payload),
    [actions, editView]
  );
  const onScheduleSubmit = useCallback(
    (payload) => actions.submitSchedule(schedView, payload),
    [actions, schedView]
  );

  const isMobile = useIsMobile();

  if (!onSaveablePage && views.length === 0) return null;

  const trigger = (
    <Button variant="outline" size="sm" className="gap-1.5 h-9" data-testid="saved-views-trigger" title="Saved views">
      <Bookmark className="h-4 w-4" />
      <span className="hidden md:inline text-xs">Views</span>
      {views.length > 0 && (
        <span className="ml-0.5 inline-flex items-center justify-center rounded-full bg-[#111565]/10 text-[#111565] text-[10px] font-semibold px-1.5 h-4">
          {views.length}
        </span>
      )}
    </Button>
  );

  const panel = (
    <>
      <div className="p-3 border-b bg-muted/40 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold">Saved views</p>
          <Button
            size="sm" variant="ghost" className="h-7 gap-1 text-xs"
            disabled={!canSaveCurrent}
            onClick={() => { setOpen(false); setSaveDialogOpen(true); }}
            data-testid="save-current-view-btn"
            title={canSaveCurrent ? 'Save the current filters as a named view' : 'Apply some filters first to enable saving'}
          >
            <BookmarkPlus className="h-3.5 w-3.5" /> Save this view
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
                placeholder="Search views, tags, folders…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
                data-testid="saved-views-search"
              />
            </div>
            {!canSaveCurrent && onSaveablePage && (
              <p className="text-[11px] text-muted-foreground">
                Apply filters on this page to enable saving.
              </p>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {visibleViews.length === 0 ? (
              <div className="p-6 text-center space-y-3">
                <Bookmark className="h-8 w-8 mx-auto text-muted-foreground/40" />
                <p className="text-xs text-muted-foreground">
                  {search ? 'No views match your search.' : 'No saved views yet. Filter a list page, then click “Save this view”.'}
                </p>
                {!search && (
                  <Button
                    size="sm" variant="outline" className="text-xs h-7"
                    data-testid="seed-templates-btn"
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API_URL}/api/saved-view-templates/seed-me`, {}, { headers: authHeaders() });
                        if (data.inserted > 0) {
                          toast.success(`Added ${data.inserted} starter view${data.inserted === 1 ? '' : 's'}`);
                          fetchViews();
                        } else if (data.available === 0) {
                          toast.info('No template views are available yet — ask your admin to create some.');
                        } else {
                          toast.info('Starter views already added.');
                        }
                      } catch (err) {
                        toast.error('Could not load templates');
                      }
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Seed starter views
                  </Button>
                )}
              </div>
            ) : (
              Object.entries(grouped).map(([key, items]) => (
                <div
                  key={key}
                  className="py-2"
                  data-testid={`saved-views-group-${key.replace(/[^a-z0-9]/gi, '-')}`}
                >
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5">
                    {folderColorByKey[key] && (
                      <span
                        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: folderColorByKey[key] }}
                        data-testid={`folder-color-swatch-${key.replace(/[^a-z0-9]/gi, '-')}`}
                      />
                    )}
                    {key.startsWith('📁') ? key : routeLabel(key)}
                  </div>
                  <SortableViewGroup
                    items={items}
                    onReorder={actions.reorderView}
                    renderRow={(v) => (
                      <SavedViewRow
                        view={v}
                        isTop={v.id === topUsedId}
                        onApply={actions.applyView}
                        onOpenInNewTab={actions.openInNewTab}
                        onCopyLink={actions.copyLink}
                        onToggleSubscribe={actions.toggleSubscribe}
                        onToggleSubscriptionEmail={actions.toggleSubscriptionEmail}
                        onOpenSchedule={actions.openSchedule}
                        onSetDefault={actions.setAsDefault}
                        onClearDefault={actions.clearDefault}
                        onSetTeamDefault={actions.setTeamDefault}
                        onEdit={actions.openEdit}
                        onDelete={actions.deleteView}
                        onExportCsv={() => setExportTarget({ view: v, format: 'csv' })}
                        onExportPdf={() => setExportTarget({ view: v, format: 'pdf' })}
                        onTogglePin={actions.togglePin}
                        onToggleFavourite={actions.toggleFavourite}
                        onMoveOrder={actions.moveOrder}
                        onSetSlug={actions.setSlug}
                        onShowActivity={() => setActivityView(v)}
                        onShowAnalytics={() => setAnalyticsView(v)}
                        canSetTeamDefault={canShare}
                      />
                    )}
                  />
                </div>
              ))
            )}
          </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>{trigger}</SheetTrigger>
          <SheetContent side="bottom" className="p-0 max-h-[85vh] flex flex-col" data-testid="saved-views-panel">
            {panel}
          </SheetContent>
        </Sheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="end" className="w-96 p-0" data-testid="saved-views-panel">
            {panel}
          </PopoverContent>
        </Popover>
      )}

      <SaveViewDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        pathLabel={routeLabel(location.pathname)}
        pathname={location.pathname}
        search={location.search}
        name={name}
        setName={setName}
        shared={shared}
        setShared={setShared}
        autoSlug={autoSlug}
        setAutoSlug={setAutoSlug}
        canShare={canShare}
        saving={saving}
        onSubmit={handleSave}
      />

      <EditViewDialog
        view={editView}
        onClose={() => setEditView(null)}
        onSubmit={onEditSubmit}
      />

      <ScheduleViewDialog
        view={schedView}
        onClose={() => setSchedView(null)}
        onSubmit={onScheduleSubmit}
      />

      <ExportColumnPicker
        open={Boolean(exportTarget)}
        onClose={() => setExportTarget(null)}
        path={exportTarget?.view?.path}
        format={exportTarget?.format || 'csv'}
        onConfirm={({ columns }) => {
          if (!exportTarget?.view) return;
          if (exportTarget.format === 'pdf') actions.exportPdf(exportTarget.view, columns);
          else actions.exportCsv(exportTarget.view, columns);
        }}
      />

      <ViewActivityLogDialog view={activityView} onClose={() => setActivityView(null)} />

      <SavedViewAnalyticsModal view={analyticsView} onClose={() => setAnalyticsView(null)} />
    </>
  );
};

export default SavedViewsMenu;
